import { useMemo, useState } from "react";
import { useSnackbar } from "../../core/contexts/SnackbarContext"
import { ApiFactory } from "../../core/config/ApiFactory";
import { ReferenciaItem } from "../enum/ReferenciaItem";
import { IVendaMes } from "../interfaces/IVendaMes";
import { IConta } from "../interfaces/IConta";
import { IContaMensalRelatorio } from "../interfaces/IContaMensalRelatorio";
import { IItemMaisVendido } from "../interfaces/IItemMaisVendido";
import { IComboMaisVendido } from "../interfaces/IComboMaisVendido";

export const useContas = () => {
    const apiFinanceiro = useMemo(() => ApiFactory.getApi("MS_FINANCEIRO"), []);
    const [loading, setLoading] = useState(false);
    const snackbar = useSnackbar();

    const buscarContasPorIntervalo = async (intervaloUuid: string) => {
        if(!intervaloUuid) {
            return snackbar("Intervalo inválido", { severity: "error" });
        }
        setLoading(true);
        try {
            const { data } = await apiFinanceiro.get(`/contas/intervalo/${intervaloUuid}`);
            return data;
        } catch (error) {
            snackbar("Ocorreu um erro ao buscar as contas do intervalo", { severity: "error" })
        } finally {
            setLoading(false);
        }
    }

    type FiltroTemporal = { ano: number; dataInicio?: never; dataFim?: never } | { ano?: never; dataInicio: string; dataFim: string };

    const consultarTicketMedio = async (filtro: FiltroTemporal) => {
        setLoading(true);
        try {
            const params = filtro.dataInicio
                ? `dataInicio=${filtro.dataInicio}&dataFim=${filtro.dataFim}`
                : `ano=${filtro.ano}`;
            const { data } = await apiFinanceiro.get(`/contas/ticket-medio?${params}`);
            return data;
        } catch (error) {
            snackbar("Ocorreu um erro ao buscar o ticket médio", { severity: "error" })
        } finally {
            setLoading(false);
        }
    }

    const consultarValorTotal = async (referenciaTipo: ReferenciaItem, filtro: FiltroTemporal) => {
        setLoading(true);
        try {
            const params = filtro.dataInicio
                ? `dataInicio=${filtro.dataInicio}&dataFim=${filtro.dataFim}`
                : `ano=${filtro.ano}`;
            const { data } = await apiFinanceiro.get(`/contas/valor-total/${referenciaTipo}?${params}`);
            return data;
        } catch (error) {
            snackbar("Ocorreu um erro ao buscar o valor total", { severity: "error" })
        } finally {
            setLoading(false);
        }
    }

    const consultarVendasMes = async (filtro: FiltroTemporal): Promise<IVendaMes[] | undefined> => {
        setLoading(true);
        try {
            const params = filtro.dataInicio
                ? `dataInicio=${filtro.dataInicio}&dataFim=${filtro.dataFim}`
                : `ano=${filtro.ano}`;
            const { data } = await apiFinanceiro.get(`/contas/valor-mes?${params}`);
            return data;
        } catch (error) {
            snackbar("Ocorreu um erro ao buscar o valor total", { severity: "error" })
        } finally {
            setLoading(false);
        }
    }

    const criarContaManual = async (conta: Omit<IConta, 'uuid' | 'itens' | 'anexos' | 'dataHora' | 'referenciaTipo' | 'referenciaUuid' | 'caixaUuid' | 'pagamentos'>): Promise<IConta | undefined> => {
        setLoading(true);
        try {
            const { data } = await apiFinanceiro.post(`/contas/manuais`, conta);
            snackbar("Conta criada com sucesso");
            return data;
        } catch (error) {
            snackbar("Ocorreu um erro ao criar a conta", { severity: "error" })
        } finally {
            setLoading(false);
        }
    }

    const editarContaManual = async (uuid: string, dados: Partial<IConta>): Promise<IConta | undefined> => {
        setLoading(true);
        try {
            const { data } = await apiFinanceiro.put(`/contas/manuais/${uuid}`, dados);
            snackbar("Conta atualizada com sucesso");
            return data;
        } catch (error) {
            snackbar("Ocorreu um erro ao atualizar a conta", { severity: "error" })
        } finally {
            setLoading(false);
        }
    }

    const excluirContaManual = async (uuid: string): Promise<boolean> => {
        setLoading(true);
        try {
            await apiFinanceiro.delete(`/contas/manuais/${uuid}`);
            snackbar("Conta excluída com sucesso");
            return true;
        } catch (error) {
            snackbar("Ocorreu um erro ao excluir a conta", { severity: "error" })
            return false;
        } finally {
            setLoading(false);
        }
    }

    const buscarItensMaisVendidos = async (dataInicio?: string, dataFim?: string, referenciaTipo?: string): Promise<IItemMaisVendido[] | undefined> => {
        setLoading(true);
        try {
            let url = `/contas/itens-mais-vendidos`;
            const params: string[] = [];
            if (dataInicio) params.push(`dataInicio=${dataInicio}`);
            if (dataFim) params.push(`dataFim=${dataFim}`);
            if (referenciaTipo) params.push(`referenciaTipo=${referenciaTipo}`);
            if (params.length) url = `${url}?${params.join('&')}`;
            const { data } = await apiFinanceiro.get(url);
            return data;
        } catch (error) {
            snackbar("Ocorreu um erro ao buscar os itens mais vendidos", { severity: "error" })
        } finally {
            setLoading(false);
        }
    }

    const buscarCombosMaisVendidos = async (dataInicio?: string, dataFim?: string): Promise<IComboMaisVendido[] | undefined> => {
        setLoading(true);
        try {
            let url = `/contas/combos-mais-vendidos`;
            const params: string[] = [];
            if (dataInicio) params.push(`dataInicio=${dataInicio}`);
            if (dataFim) params.push(`dataFim=${dataFim}`);
            if (params.length) url = `${url}?${params.join('&')}`;
            const { data } = await apiFinanceiro.get(url);
            return data;
        } catch (error) {
            snackbar("Ocorreu um erro ao buscar os combos mais vendidos", { severity: "error" })
        } finally {
            setLoading(false);
        }
    }

    const buscarContasMensais = async (ano: number, tipo?: string, pago?: boolean): Promise<IContaMensalRelatorio[] | undefined> => {
        setLoading(true);
        try {
            let url = `/contas/relatorio-mensal?ano=${ano}`;
            if (tipo) url = `${url}&tipo=${tipo}`;
            if (pago !== undefined) url = `${url}&pago=${pago}`;
            const { data } = await apiFinanceiro.get(url);
            return data;
        } catch (error) {
            snackbar("Ocorreu um erro ao buscar o relatório mensal", { severity: "error" })
        } finally {
            setLoading(false);
        }
    }

    const baixarBlob = (blob: Blob, nomeArquivo: string) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', nomeArquivo);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const exportarContas = async (params: { tipo?: string; pago?: string; descricao?: string; dataInicio?: string; dataFim?: string }) => {
        setLoading(true);
        try {
            const query = new URLSearchParams();
            if (params.tipo) query.append('tipo', params.tipo);
            if (params.pago !== undefined && params.pago !== '') query.append('pago', params.pago);
            if (params.descricao) query.append('descricao', params.descricao);
            if (params.dataInicio) query.append('dataInicio', params.dataInicio);
            if (params.dataFim) query.append('dataFim', params.dataFim);

            const { data } = await apiFinanceiro.get(`/contas/manuais/exportar?${query.toString()}`, { responseType: 'blob' });
            baixarBlob(data, 'contas.xlsx');
        } catch (error) {
            snackbar("Ocorreu um erro ao exportar as contas", { severity: "error" })
        } finally {
            setLoading(false);
        }
    }

    const exportarContasMensais = async (ano: number, tipo?: string, pago?: boolean) => {
        setLoading(true);
        try {
            const query = new URLSearchParams({ ano: String(ano) });
            if (tipo) query.append('tipo', tipo);
            if (pago !== undefined) query.append('pago', String(pago));

            const { data } = await apiFinanceiro.get(`/contas/relatorio-mensal/exportar?${query.toString()}`, { responseType: 'blob' });
            baixarBlob(data, `contas-mensais-${ano}.xlsx`);
        } catch (error) {
            snackbar("Ocorreu um erro ao exportar o relatório mensal", { severity: "error" })
        } finally {
            setLoading(false);
        }
    }

    const exportarItensMaisVendidos = async (dataInicio?: string, dataFim?: string, referenciaTipo?: string) => {
        setLoading(true);
        try {
            const query = new URLSearchParams();
            if (dataInicio) query.append('dataInicio', dataInicio);
            if (dataFim) query.append('dataFim', dataFim);
            if (referenciaTipo) query.append('referenciaTipo', referenciaTipo);

            const { data } = await apiFinanceiro.get(`/contas/itens-mais-vendidos/exportar?${query.toString()}`, { responseType: 'blob' });
            baixarBlob(data, 'itens-mais-vendidos.xlsx');
        } catch (error) {
            snackbar("Ocorreu um erro ao exportar os itens mais vendidos", { severity: "error" })
        } finally {
            setLoading(false);
        }
    }

    return {
        buscarContasPorIntervalo,
        loading,
        consultarValorTotal,
        consultarTicketMedio,
        consultarVendasMes,
        criarContaManual,
        editarContaManual,
        excluirContaManual,
        buscarItensMaisVendidos,
        buscarCombosMaisVendidos,
        buscarContasMensais,
        exportarContas,
        exportarContasMensais,
        exportarItensMaisVendidos,
    }
}
